import React, {useCallback, useRef, useState} from "react";
import Dialog from "../../base/dialog";
import {Col, Row} from "reactstrap";
import {DialogActions, DialogContent, DialogTitle} from "@mui/material";
import Button from "../../base/button/button";
import {CustomerAddressTypes} from "../../../../core/constants/enums";
import {CheckboxForm} from "../../base/checkbox/checkbox";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import {createUUId} from "../../../../core/services/utils/utils";
import {OutlinedInputForm} from "../../base/input/outlined_input";
import Form from "../../base/form";
import {MuiRFF, Yup} from '../../form';
import useAccessLevels from "../../../hooks/use-access-levels";


const schema = Yup.object().shape({
    title: Yup.string().nullable().trim(),
    isDefault: Yup.boolean().nullable().default(false),
})

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const SaveOrderAddressDialog = ({open, close, typeId, address, onAddressCreated, customerId, customerName}) => {
    const [saving, setSaving] = useState(false);
    const isMounted = useIsMounted();
    const {packages} = useAccessLevels();
    const canManageAddresses = packages.sales.manageCustomerAddresses;
    const formIdRef = useRef(createUUId(true));

    /**
     * Creates a new address with the provided values of the address and if the api is successful, closes the modal
     *
     * @param {string} values
     * @return {Promise<void>}
     */
    const saveAddressForLater = useCallback(async (values) => {
        if (!canManageAddresses) return;
        setSaving(true);
        const forApi = {
            ...address,
            streetAddress: address.fullAddress,
            isDefault: !!values.isDefault,
            title: values.title,
            type: {id: typeId},
            customer: {
                id: customerId
            },
        };
        const response = await Api.createUserAddress(forApi);
        if (!isMounted())
            return;
        setSaving(false);
        if (!response?.resultFlag) {
            return;
        }
        if ((await onAddressCreated(response.data))) {
            close();
        }
    }, [address, canManageAddresses, close, customerId, isMounted, onAddressCreated, typeId]);

    return (
        <Dialog
            open={open}
            setOpen={close}
            maxWidth={'sm'}
            className={'bound-dialog'}
        >
            <DialogTitle className={'p-0'} id="scroll-dialog-title">
                <p className={'text-lg text-color-1 p-4 font-weight-semi-bold text-center'}>
                    Save Order Address Information
                </p>
            </DialogTitle>
            <DialogContent dividers className={'p-4'}>
                <p className={'text-sm text-color-3'}>
                    Do you want to save this address in {customerName ?? ''}'s <span
                    className={'black font-weight-semi-bold'}>{typeId === CustomerAddressTypes.billing ? 'Billing' : 'Shipping'}
                </span> addresses?
                </p>
                <Form
                    onSubmit={saveAddressForLater}
                    initialValues={{}}
                    id={formIdRef.current}
                    validate={validate}
                    className='w-100 mt-4'
                    render={({values}) => {
                        return (
                            <Row className='no-gutters justify-content-center w-100'>
                                <Col xs={12} className={'mb-3'}>
                                    <OutlinedInputForm
                                        label={'Title'}
                                        name={`title`}
                                        required={required.title}
                                    />
                                </Col>
                                <Col xs={12} className={'row no-gutters justify-content-start'}>
                                    <CheckboxForm
                                        name={'isDefault'}
                                        required={required.isDefault}
                                        data={{
                                            value: values?.isDefault ?? false,
                                            label: 'Set as the default address'
                                        }}
                                    />
                                </Col>
                            </Row>
                        );
                    }}
                />
            </DialogContent>
            <DialogActions className={'p-0 d-flex w-100 justify-content-between'}>
                <Button
                    disabled={!open || saving}
                    variant={'dialog'}
                    color={'primary'}
                    onClick={() => close()}
                    borderRadius={'0 8px 0 8px'}
                >
                    Close
                </Button>
                <Button
                    disabled={!open || saving}
                    variant={'dialog'}
                    color={'primary'}
                    type={'submit'}
                    form={formIdRef.current}
                    borderRadius={'8px 0 8px 0'}
                >
                    {
                        saving
                            ? 'Saving...'
                            : 'Save'
                    }
                </Button>
            </DialogActions>
        </Dialog>
    );
}


export default SaveOrderAddressDialog;
